import type { UIMessage } from 'ai';

/**
 * One thing the assistant did (or is doing) during a turn, derived from the
 * tool-call parts of the streaming assistant message. `labelKey` is an i18n key
 * under `assistant.activity.steps.*`.
 */
export type ActivityStep = {
  id: string;
  labelKey: string;
  status: 'active' | 'done' | 'error';
};

/**
 * Tool name → i18n key suffix. Covers the assistant's own tools, the
 * email-retrieval delegation (`agent-emailRetrievalAgent`, the tool name Mastra
 * exposes for a native sub-agent), and the retrieval sub-agent's leaf tools —
 * its chunks are forwarded into the same UI stream, so they surface here too.
 * Anything unmapped falls back to a generic label rather than leaking a raw
 * tool name into the UI.
 *
 * These keys are resolved dynamically (`t(\`…steps.${key}\`)`), so
 * `i18next-cli` cannot see them: keep `assistant.activity.steps.*` in every
 * locale file by hand, and don't let a key prune remove them. `ActivityTrail`'s
 * test renders against the real resources and fails if one goes missing.
 */
const STEP_KEY_BY_TOOL: Readonly<Record<string, string>> = {
  'agent-emailRetrievalAgent': 'searchingMailbox',
  createAndSaveDraft: 'savingDraft',
  createCategory: 'creatingCategory',
  createFolder: 'creatingFolder',
  draftEmail: 'writingDraft',
  fetchNextPage: 'loadingMoreResults',
  filterMessages: 'filteringEmails',
  getAllCategories: 'checkingCategories',
  getCachedFolders: 'checkingFolders',
  getEmailById: 'readingEmail',
  getEmailsInFolder: 'checkingFolder',
  getMessageBody: 'readingEmail',
  listMailFolders: 'checkingFolders',
  listRecentMessages: 'listingRecentEmails',
  moveEmailToFolder: 'movingEmail',
  readAttachment: 'readingAttachment',
  readCalendarEvents: 'checkingCalendar',
  renameFolder: 'renamingFolder',
  reviseEmail: 'revisingDraft',
  searchAttachment: 'searchingAttachment',
  searchMessagesByKeyword: 'searchingKeywords',
  sendEmail: 'sendingEmail',
  sendWebhook: 'callingWebhook',
};

export const FALLBACK_STEP_KEY = 'working';

type LooseToolPart = {
  state?: string;
  toolCallId?: string;
  toolName?: string;
  type?: string;
};

/**
 * Tool parts arrive as `{ type: 'dynamic-tool', toolName }` (we register no
 * tools with `useChat`) or `{ type: 'tool-<name>' }`. Accept both — same
 * reasoning as `extractDraftFromMessage`.
 */
function toolNameOf(part: unknown): string | undefined {
  if (typeof part !== 'object' || part === null) {
    return undefined;
  }
  const p = part as LooseToolPart;
  if (p.type === 'dynamic-tool') {
    return p.toolName;
  }
  if (typeof p.type === 'string' && p.type.startsWith('tool-')) {
    return p.toolName ?? p.type.slice('tool-'.length);
  }
  return undefined;
}

function statusOf(state: string | undefined): ActivityStep['status'] {
  if (state === 'output-error') {
    return 'error';
  }
  return state === 'output-available' ? 'done' : 'active';
}

/**
 * Ordered activity steps for one assistant message. Consecutive calls to the
 * same tool collapse into a single step (a retrieval loop can call
 * `filterMessages` several times in a row; the user does not need each one),
 * with the collapsed step taking the latest status.
 */
export function extractActivitySteps(msg: UIMessage): Array<ActivityStep> {
  const steps: Array<ActivityStep> = [];

  for (const [index, part] of msg.parts.entries()) {
    const toolName = toolNameOf(part);
    if (!toolName) {
      continue;
    }
    const labelKey = STEP_KEY_BY_TOOL[toolName] ?? FALLBACK_STEP_KEY;
    const status = statusOf((part as LooseToolPart).state);
    const previous = steps.at(-1);
    if (previous?.labelKey === labelKey) {
      previous.status = status;
      continue;
    }
    steps.push({
      id: (part as LooseToolPart).toolCallId ?? `${toolName}-${index}`,
      labelKey,
      status,
    });
  }

  return steps;
}

/**
 * The step to surface in the banner: whatever is still running, else the last
 * thing that happened. `undefined` when the turn hasn't called a tool yet, so
 * the banner keeps its plain "Thinking…" label.
 */
export function currentActivityStep(steps: Array<ActivityStep>): ActivityStep | undefined {
  return steps.findLast((s) => s.status === 'active') ?? steps.at(-1);
}
